/*
 * dm-product.js
 * <dm-product> custom element: variant selection computed from the real variants array,
 * gallery with variant image switching, zoom dialog, quantity, add to cart and the sticky
 * mobile purchase bar. Option positions come from the product JSON (never hard-coded).
 */
(function () {
  'use strict';

  var DM = window.DM;
  if (!DM || !window.customElements || customElements.get('dm-product')) return;
  var str = DM.strings;

  function plugText(value) {
    return String(value).replace(/US Plug\s*110V/i, 'US Plug • 110V');
  }

  class DMProduct extends HTMLElement {
    connectedCallback() {
      if (this.__ready) return;
      this.__ready = true;
      this.init();
    }

    disconnectedCallback() {
      if (this.io) this.io.disconnect();
      if (this.onPageShow) window.removeEventListener('pageshow', this.onPageShow);
      this.__ready = false;
    }

    init() {
      var json = this.querySelector('[data-dm-product-json]');
      try {
        this.data = JSON.parse(json.textContent);
      } catch (e) {
        return;
      }
      this.form = this.querySelector('[data-dm-form]');
      this.fieldsets = DM.$$('[data-dm-option]', this);
      this.variantInput = this.querySelector('[data-dm-variant-id]');
      this.priceEl = this.querySelector('[data-dm-price]');
      this.addBtn = this.querySelector('[data-dm-add]');
      this.addLabel = this.querySelector('[data-dm-add-label]');
      this.errorEl = this.querySelector('[data-dm-error]');
      this.noticeEl = this.querySelector('[data-dm-notice]');
      this.qtyInput = this.querySelector('[data-dm-qty]');
      this.sticky = this.querySelector('[data-dm-sticky]');
      this.lowStockEl = this.querySelector('[data-dm-lowstock]');
      this.compareEl = this.querySelector('[data-dm-compare]');
      this.comparePriceEl = this.querySelector('[data-dm-compare-price]');
      this.saveEl = this.querySelector('[data-dm-save]');
      this.buyNow = this.getAttribute('data-buy-action') === 'buy_now';
      this.buyLabel = this.getAttribute('data-buy-label') || 'Buy Now';
      this.buyShort = this.getAttribute('data-buy-short') || this.buyLabel;
      this.adding = false;
      this.syncedMedia = null;

      this.sel = this.readSelection();
      this.bindOptions();
      this.bindQuantity();
      this.bindForm();
      this.initGallery();
      this.initSticky();

      this.onPageShow = function (event) {
        if (event.persisted) {
          this.adding = false;
          this.renderButton();
        }
      }.bind(this);
      window.addEventListener('pageshow', this.onPageShow);

      this.update(false);
      this.renderDelivery();
    }

    /* ---------- selection model ---------- */
    readSelection() {
      var sel = this.data.options.map(function () {
        return null;
      });
      if (!this.fieldsets.length && this.data.variants.length === 1) {
        return this.data.variants[0].options.slice();
      }
      this.fieldsets.forEach(function (fs) {
        var i = parseInt(fs.getAttribute('data-option-index'), 10);
        var checked = fs.querySelector('.dm-opt__input:checked');
        if (checked) sel[i] = checked.value;
      });
      return sel;
    }

    /* State of one option value, looking only at options chosen before it. */
    valueState(i, value) {
      var sel = this.sel;
      var list = this.data.variants.filter(function (v) {
        if (v.options[i] !== value) return false;
        for (var j = 0; j < i; j++) {
          if (sel[j] !== null && v.options[j] !== sel[j]) return false;
        }
        return true;
      });
      if (!list.length) return 'unavailable';
      return list.some(function (v) {
        return v.available;
      })
        ? 'ok'
        : 'soldout';
    }

    matching() {
      var sel = this.sel;
      return this.data.variants.filter(function (v) {
        return sel.every(function (s, i) {
          return s === null || v.options[i] === s;
        });
      });
    }

    firstMissing() {
      for (var i = 0; i < this.sel.length; i++) {
        if (this.sel[i] === null) return i;
      }
      return -1;
    }

    currentVariant() {
      if (this.firstMissing() !== -1) return null;
      return this.matching()[0] || null;
    }

    optionName(i) {
      return this.data.options[i] ? this.data.options[i].name : '';
    }

    /* ---------- events ---------- */
    bindOptions() {
      var self = this;
      this.addEventListener('change', function (event) {
        var input = event.target;
        if (!(input instanceof HTMLInputElement) || !input.classList.contains('dm-opt__input')) return;
        if (!self.contains(input) || !input.closest('[data-dm-option]')) return;
        var i = parseInt(input.getAttribute('data-option-index'), 10);
        self.sel[i] = input.value;
        self.hideError();
        self.clearIncompatible(i);
        self.update(true);
      });
    }

    /* Changing an earlier option never silently changes a later one: clear it and ask. */
    clearIncompatible(changed) {
      var notices = [];
      for (var j = changed + 1; j < this.sel.length; j++) {
        if (this.sel[j] !== null && this.valueState(j, this.sel[j]) !== 'ok') {
          notices.push(
            DM.fmt(str.optionCleared, {
              value: plugText(this.sel[j]),
              other: plugText(this.sel[changed]),
              option: this.optionName(j).toLowerCase()
            })
          );
          this.sel[j] = null;
        }
      }
      if (notices.length) {
        this.showNotice(notices.join(' '));
      } else {
        this.hideNotice();
      }
    }

    bindQuantity() {
      var self = this;
      if (!this.qtyInput) return;
      var clamp = function () {
        var n = parseInt(self.qtyInput.value, 10);
        if (isNaN(n) || n < 1) n = 1;
        if (n > 99) n = 99;
        self.qtyInput.value = n;
        return n;
      };
      var minus = this.querySelector('[data-dm-qty-minus]');
      var plus = this.querySelector('[data-dm-qty-plus]');
      if (minus)
        minus.addEventListener('click', function () {
          self.qtyInput.value = clamp() - 1;
          clamp();
        });
      if (plus)
        plus.addEventListener('click', function () {
          self.qtyInput.value = clamp() + 1;
          clamp();
        });
      this.qtyInput.addEventListener('change', clamp);
    }

    bindForm() {
      var self = this;
      if (this.form) {
        this.form.addEventListener('submit', function (event) {
          event.preventDefault();
          self.onAdd();
        });
      }
    }

    quantity() {
      var n = this.qtyInput ? parseInt(this.qtyInput.value, 10) : 1;
      return isNaN(n) || n < 1 ? 1 : n;
    }

    focusMissing(index) {
      var fs = this.fieldsets.filter(function (f) {
        return parseInt(f.getAttribute('data-option-index'), 10) === index;
      })[0];
      if (!fs) return;
      var target = fs.querySelector('.dm-opt__input:not([disabled])');
      fs.scrollIntoView({ block: 'center', behavior: DM.reducedMotion() ? 'auto' : 'smooth' });
      if (target) target.focus({ preventScroll: true });
    }

    onAdd() {
      if (this.adding || !this.data.available) return;
      var missing = this.firstMissing();
      if (missing !== -1) {
        this.showError(DM.fmt(str.chooseOptionError, { option: this.optionName(missing).toLowerCase() }));
        this.focusMissing(missing);
        return;
      }
      var variant = this.currentVariant();
      if (!variant || !variant.available) return;
      this.hideError();
      this.setAdding(true);
      var self = this;
      if (!window.DMCart) {
        this.setAdding(false);
        this.showError(str.addError);
        return;
      }
      window.DMCart.add([{ id: variant.id, quantity: this.quantity() }], { source: 'product', open: !this.buyNow })
        .then(function () {
          if (self.buyNow) {
            /* Buy Now: the variant is in the cart, continue to the real Shopify checkout. */
            var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
            window.location.assign(root + 'checkout');
            return;
          }
          self.setAdding(false);
        })
        .catch(function (err) {
          self.setAdding(false);
          if (err && err.message === 'busy') return;
          var detail = err && err.data && err.data.description ? ' ' + err.data.description : '';
          self.showError(str.addError + detail);
        });
    }

    setAdding(value) {
      this.adding = value;
      if (this.addBtn) this.addBtn.setAttribute('aria-busy', value ? 'true' : 'false');
      this.renderButton();
    }

    /* ---------- rendering ---------- */
    update(userChange) {
      var self = this;
      this.fieldsets.forEach(function (fs) {
        var i = parseInt(fs.getAttribute('data-option-index'), 10);
        DM.$$('.dm-opt__input', fs).forEach(function (input) {
          var state = self.valueState(i, input.value);
          var checked = self.sel[i] === input.value;
          var label = fs.querySelector('label[for="' + input.id + '"]');
          input.checked = checked;
          input.disabled = state !== 'ok' && !checked;
          if (label) {
            label.classList.toggle('is-unavailable', state !== 'ok');
            var sr = label.querySelector('[data-dm-state]');
            if (sr) {
              sr.textContent = state === 'soldout' ? ' ' + str.soldOutSr : state === 'unavailable' ? ' ' + str.unavailableSr : '';
            }
          }
        });
        var valueEl = fs.querySelector('[data-dm-opt-value]');
        if (valueEl) valueEl.textContent = self.sel[i] !== null ? plugText(self.sel[i]) : str.select || '';
      });

      var variant = this.currentVariant();
      if (this.variantInput) this.variantInput.value = variant ? variant.id : '';
      this.renderPrice();
      this.renderButton();
      this.renderLowStock(variant);
      this.renderFit();
      this.syncMedia(userChange);
      if (userChange && variant) this.updateUrl(variant);
    }

    /* "Almost out of stock": only for the selected variant when Shopify tracks its inventory, it cannot be
       sold after it reaches zero, and 1 to lowStockMax units are left. Never shown for untracked or
       continue-selling variants, and never a made-up counter. */
    renderLowStock(variant) {
      if (!this.lowStockEl) return;
      var max = parseInt(this.data.lowStockMax, 10) || 5;
      var show = !!(
        variant &&
        variant.available &&
        variant.tracked === true &&
        variant.policy === 'deny' &&
        typeof variant.qty === 'number' &&
        variant.qty >= 1 &&
        variant.qty <= max
      );
      this.lowStockEl.hidden = !show;
    }

    /* Highlights the chart row of the selected size. */
    renderFit() {
      var rows = DM.$$('[data-dm-fit] [data-fit-size]', this);
      if (!rows.length) return;
      var chosen = null;
      for (var i = 0; i < this.data.options.length; i++) {
        if (String(this.data.options[i].name).toLowerCase() === 'size') chosen = this.sel[i];
      }
      rows.forEach(function (row) {
        row.classList.toggle('is-selected', chosen !== null && row.getAttribute('data-fit-size') === chosen);
      });
    }

    renderPrice() {
      if (!this.priceEl) return;
      var list = this.matching();
      if (!list.length) return;
      var prices = list.map(function (v) {
        return v.price;
      });
      var min = Math.min.apply(null, prices);
      var max = Math.max.apply(null, prices);
      this.priceEl.textContent = min === max ? DM.money(min) : DM.fmt(str.from, { price: DM.money(min) });
      this.renderCompare(list);
    }

    /* Crossed-out price and savings badge: only for one fully selected variant whose compare-at price is
       higher than its price. Percentage = (compare - price) / compare, rounded. Never invented. */
    renderCompare(list) {
      if (!this.compareEl || !this.saveEl) return;
      var show = false;
      var cmp = 0;
      var pct = 0;
      var upto = list.length > 1;
      var valid =
        list.length > 0 &&
        list.every(function (v) {
          return v.compare && v.compare > v.price;
        });
      if (valid) {
        var low = list.slice().sort(function (a, b) {
          return a.price - b.price;
        })[0];
        cmp = low.compare;
        list.forEach(function (v) {
          pct = Math.max(pct, Math.round(((v.compare - v.price) / v.compare) * 100));
        });
        show = pct >= 1;
      }
      this.compareEl.hidden = !show;
      this.saveEl.hidden = !show;
      if (show) {
        if (this.comparePriceEl) this.comparePriceEl.textContent = DM.money(cmp);
        this.saveEl.textContent = (this.saveEl.getAttribute('data-label') || 'SAVE') + (upto ? ' UP TO ' : ' ') + pct + '%';
      }
    }

    /* Delivery estimate: processing + transit in business days (Mon to Fri) from today in the store's
       time zone. Only runs when the section has verified transit days; otherwise the element is absent. */
    renderDelivery() {
      var el = this.querySelector('[data-dm-delivery]');
      if (!el) return;
      var d = el.dataset;
      var tmin = parseInt(d.tmin, 10);
      var tmax = parseInt(d.tmax, 10);
      if (isNaN(tmin) || isNaN(tmax)) return;
      var pmin = parseInt(d.pmin, 10) || 0;
      var pmax = parseInt(d.pmax, 10) || 0;
      var cutoff = parseInt(d.cutoff, 10);
      var y, m, day, hour;
      try {
        var parts = new Intl.DateTimeFormat('en-US', { timeZone: d.tz || 'America/New_York', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', hourCycle: 'h23' }).formatToParts(new Date());
        var get = function (t) {
          return parseInt(parts.filter(function (p) { return p.type === t; })[0].value, 10);
        };
        y = get('year'); m = get('month'); day = get('day'); hour = get('hour');
      } catch (e) {
        var n = new Date();
        y = n.getFullYear(); m = n.getMonth() + 1; day = n.getDate(); hour = n.getHours();
      }
      var start = new Date(Date.UTC(y, m - 1, day));
      var isBiz = function (dt) { var w = dt.getUTCDay(); return w !== 0 && w !== 6; };
      var addDays = function (dt, n) {
        var r = new Date(dt.getTime());
        var left = n;
        while (left > 0) {
          r.setUTCDate(r.getUTCDate() + 1);
          if (isBiz(r)) left--;
        }
        return r;
      };
      var nextBiz = function (dt) {
        var r = new Date(dt.getTime());
        do { r.setUTCDate(r.getUTCDate() + 1); } while (!isBiz(r));
        return r;
      };
      var cal = d.cal === '1';
      var addCal = function (dt, n) {
        var r = new Date(dt.getTime());
        r.setUTCDate(r.getUTCDate() + n);
        return r;
      };
      if (cal) {
        if (!isNaN(cutoff) && hour >= cutoff) start = addCal(start, 1);
      } else if (!isBiz(start) || (!isNaN(cutoff) && hour >= cutoff)) {
        start = nextBiz(start);
      }
      var from = cal ? addCal(start, pmin + tmin) : addDays(start, pmin + tmin);
      var to = cal ? addCal(start, pmax + tmax) : addDays(start, pmax + tmax);
      var fmt = function (dt) { return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' }); };
      var range;
      if (from.getTime() === to.getTime()) range = fmt(from);
      else if (from.getUTCMonth() === to.getUTCMonth()) range = fmt(from) + '\u2013' + to.getUTCDate();
      else range = fmt(from) + '\u2013' + fmt(to);
      var text = el.querySelector('[data-dm-delivery-text]');
      if (!text) return;
      text.textContent = (d.template || 'Order today \u2014 estimated delivery [dates].').replace('[dates]', range);
      el.hidden = false;
    }

    buttonState() {
      if (!this.data.available) return { label: str.soldOut, disabled: true, short: str.soldOut };
      if (this.adding) return { label: str.adding, disabled: true, short: str.adding };
      var missing = this.firstMissing();
      if (missing !== -1) {
        var name = this.optionName(missing);
        return {
          label: DM.fmt(str.selectOption, { option: name.toLowerCase() }),
          disabled: false,
          short: DM.fmt(str.chooseOption, { option: name.toLowerCase() }),
          missing: missing
        };
      }
      var variant = this.currentVariant();
      if (!variant) return { label: str.unavailable, disabled: true, short: str.unavailable };
      if (!variant.available) return { label: str.soldOut, disabled: true, short: str.soldOut };
      if (this.buyNow) return { label: this.buyLabel + ' \u00b7 ' + DM.money(variant.price), disabled: false, short: this.buyShort };
      var sep = this.classList.contains('dm-pdp--v2') ? ' \u00b7 ' : ' • ';
      return { label: str.addToCart + sep + DM.money(variant.price), disabled: false, short: str.addToCartShort };
    }

    renderButton() {
      var state = this.buttonState();
      this.btnState = state;
      if (this.addLabel) this.addLabel.textContent = state.label;
      if (this.addBtn) {
        this.addBtn.disabled = state.disabled;
        this.addBtn.classList.toggle('is-adding', !!this.adding);
      }
      this.renderSticky(state);
    }

    renderSticky(state) {
      if (!this.sticky) return;
      var price = this.sticky.querySelector('[data-dm-sticky-price]');
      var opts = this.sticky.querySelector('[data-dm-sticky-options]');
      var btn = this.sticky.querySelector('[data-dm-sticky-add]');
      if (price && this.priceEl) price.textContent = this.priceEl.textContent;
      var cmpS = this.sticky.querySelector('[data-dm-sticky-compare]');
      if (cmpS) {
        var showCmp = !!(this.compareEl && !this.compareEl.hidden && this.comparePriceEl);
        cmpS.hidden = !showCmp;
        if (showCmp) cmpS.textContent = this.comparePriceEl.textContent;
      }
      if (opts) {
        opts.textContent = this.sel
          .filter(function (s) {
            return s !== null && s !== 'Default Title';
          })
          .map(plugText)
          .join(' / ');
      }
      if (btn) {
        btn.textContent = state.short;
        btn.disabled = state.disabled;
      }
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
    }
    hideError() {
      if (this.errorEl) {
        this.errorEl.hidden = true;
        this.errorEl.textContent = '';
      }
    }
    showNotice(message) {
      if (!this.noticeEl) return;
      this.noticeEl.textContent = message;
      this.noticeEl.hidden = false;
      DM.announce(message);
    }
    hideNotice() {
      if (this.noticeEl) {
        this.noticeEl.hidden = true;
        this.noticeEl.textContent = '';
      }
    }

    updateUrl(variant) {
      try {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState(null, '', url.toString());
      } catch (e) {
        /* ignore */
      }
    }

    /* ---------- gallery ---------- */
    initGallery() {
      var self = this;
      this.track = this.querySelector('[data-dm-track]');
      this.thumbsEl = this.querySelector('[data-dm-thumbs]');
      if (!this.track) return;
      this.activeIndex = 0;
      this.appliedColor = this.data.galleryByColor ? this.data.defaultColor || null : null;

      DM.$$('[data-dm-thumb]', this).forEach(function (thumb) {
        thumb.addEventListener('click', function () {
          self.showMedia(thumb.getAttribute('data-media-id'), false);
        });
      });
      var prev = this.querySelector('[data-dm-gallery-prev]');
      var next = this.querySelector('[data-dm-gallery-next]');
      if (prev)
        prev.addEventListener('click', function () {
          self.step(-1);
        });
      if (next)
        next.addEventListener('click', function () {
          self.step(1);
        });

      var ticking = false;
      this.track.addEventListener(
        'scroll',
        function () {
          if (ticking) return;
          ticking = true;
          window.requestAnimationFrame(function () {
            ticking = false;
            var width = self.track.clientWidth || 1;
            self.setActive(Math.round(self.track.scrollLeft / width));
          });
        },
        { passive: true }
      );

      DM.$$('[data-dm-zoom]', this).forEach(function (btn) {
        btn.addEventListener('click', function () {
          self.openZoom(btn);
        });
      });
      this.setActive(0);
    }

    visibleSlides() {
      return DM.$$('.dm-gallery__slide', this.track).filter(function (el) {
        return !el.hidden;
      });
    }

    visibleThumbItems() {
      if (!this.thumbsEl) return [];
      return DM.$$('.dm-thumbs__item', this.thumbsEl).filter(function (el) {
        return !el.hidden;
      });
    }

    step(direction) {
      var count = this.visibleSlides().length;
      var index = Math.max(0, Math.min(count - 1, this.activeIndex + direction));
      this.scrollToIndex(index, false);
    }

    scrollToIndex(index, instant) {
      var slide = this.visibleSlides()[index];
      if (!slide) return;
      this.track.scrollTo({ left: slide.offsetLeft, behavior: instant || DM.reducedMotion() ? 'auto' : 'smooth' });
      this.setActive(index);
    }

    showMedia(mediaId, instant) {
      if (!this.track) return;
      var slides = this.visibleSlides();
      for (var i = 0; i < slides.length; i++) {
        if (String(slides[i].getAttribute('data-media-id')) === String(mediaId)) {
          this.scrollToIndex(i, instant);
          return;
        }
      }
    }

    setActive(index) {
      var slides = this.visibleSlides();
      if (index < 0 || index >= slides.length) return;
      this.activeIndex = index;
      var slideId = slides[index].getAttribute('data-media-id');
      var activeThumb = null;
      DM.$$('[data-dm-thumb]', this).forEach(function (thumb) {
        var on = thumb.getAttribute('data-media-id') === slideId;
        thumb.classList.toggle('is-active', on);
        if (on) {
          thumb.setAttribute('aria-current', 'true');
          activeThumb = thumb;
        } else {
          thumb.removeAttribute('aria-current');
        }
      });
      if (activeThumb && this.thumbsEl) {
        var item = activeThumb.closest('.dm-thumbs__item') || activeThumb;
        var left = item.offsetLeft - (this.thumbsEl.clientWidth - item.offsetWidth) / 2;
        this.thumbsEl.scrollTo({ left: left, behavior: 'auto' });
      }
    }

    /*
     * Color-aware gallery. A media belongs to a color when its alt text names it (data-colors).
     * Order: that color's front image, the rest of that color, shared media; other colors hidden.
     */
    applyGalleryColor() {
      if (!this.track || !this.data.galleryByColor || this.data.colorIndex === null) return;
      var ci = this.data.colorIndex;
      var color = this.sel[ci];
      if (!color || color === this.appliedColor) return;
      this.appliedColor = color;
      var key = String(color).toLowerCase();
      var front = null;
      for (var i = 0; i < this.data.variants.length; i++) {
        var v = this.data.variants[i];
        if (v.options[ci] === color && v.media) {
          front = v.media;
          break;
        }
      }
      var byOrder = function (a, b) {
        return parseInt(a.getAttribute('data-order'), 10) - parseInt(b.getAttribute('data-order'), 10);
      };
      var arrange = function (host, selector) {
        var items = DM.$$(selector, host).sort(byOrder);
        var pinned = [];
        var first = [];
        var matched = [];
        var shared = [];
        var other = [];
        items.forEach(function (li) {
          var colors = (li.getAttribute('data-colors') || '').split(',').filter(Boolean);
          if (li.getAttribute('data-pinned') === 'true') pinned.push(li);
          else if (front !== null && String(li.getAttribute('data-media-id')) === String(front)) first.push(li);
          else if (!colors.length) shared.push(li);
          else if (colors.indexOf(key) > -1) matched.push(li);
          else other.push(li);
        });
        pinned.concat(first, matched, shared).forEach(function (li) {
          li.hidden = false;
          host.appendChild(li);
        });
        other.forEach(function (li) {
          li.hidden = true;
          host.appendChild(li);
        });
      };
      arrange(this.track, '.dm-gallery__slide');
      if (this.thumbsEl) arrange(this.thumbsEl, '.dm-thumbs__item');
      this.track.scrollTo({ left: 0, behavior: 'auto' });
      this.syncedMedia = null;
      this.setActive(0);
    }

    /* Show the image that belongs to the current selection, never a conflicting one. */
    syncMedia(userChange) {
      if (!this.track) return;
      this.applyGalleryColor();
      var variant = this.currentVariant();
      var media = variant && variant.media ? variant.media : null;
      if (!media) {
        var list = this.matching();
        for (var i = 0; i < list.length; i++) {
          if (list[i].media) {
            media = list[i].media;
            break;
          }
        }
      }
      if (media && String(media) !== String(this.syncedMedia)) {
        this.syncedMedia = media;
        /* On first load keep the pinned lead images in front instead of jumping to the variant photo. */
        if (userChange === false && this.track.querySelector('[data-pinned="true"]:not([hidden])')) return;
        this.showMedia(media, true);
      }
    }

    openZoom(btn) {
      var modal = this.querySelector('[data-dm-zoom-modal]');
      var img = modal && modal.querySelector('[data-dm-zoom-img]');
      if (!modal || !img) return;
      img.src = btn.getAttribute('data-zoom-src');
      img.alt = btn.getAttribute('data-zoom-alt') || '';
      DM.modal.open(modal, btn);
    }

    /* ---------- sticky purchase bar ---------- */
    initSticky() {
      var self = this;
      if (!this.sticky || !this.addBtn || !('IntersectionObserver' in window)) return;
      this.io = new IntersectionObserver(
        function (entries) {
          var entry = entries[0];
          var past = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
          self.sticky.hidden = !past;
        },
        { threshold: 0 }
      );
      this.io.observe(this.addBtn);
      var btn = this.sticky.querySelector('[data-dm-sticky-add]');
      if (btn) {
        btn.addEventListener('click', function () {
          var state = self.btnState || self.buttonState();
          if (state.missing !== undefined) {
            self.focusMissing(state.missing);
          } else {
            self.onAdd();
          }
        });
      }
    }
  }

  customElements.define('dm-product', DMProduct);
})();
